import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Badge, useLocalizeMessage, useToastNotification } from 'libs.nucleus';
import { useApiClient, ApiClientService, AuthContext } from 'libs.react_internal';
import { appendPreviewLimitIfMissing } from '../../utils/sql_preview.utils';
export const DataTransferTemplateContext = createContext({
    timezoneOptions: [],
    packageTypeOptions: [],
    fileExtensionOptions: [],
    templateCreationOption: 'data_builder',
    setTemplateCreationOption: () => { },
    getStatusBadge: () => _jsx(_Fragment, {}),
    statusOptions: [],
    createTemplateFormData: {},
    setCreateTemplateFormData: () => { },
    isLoading: false,
    setIsLoading: () => { },
    createDataTransferTemplate: () => Promise.resolve(),
    isCreateFormValid: false,
    setIsCreateFormValid: () => { },
    defaultFormValues: {},
    checkIfTemplateNameIsValid: () => Promise.resolve(false),
    validateSQLQuery: () => Promise.resolve(false),
    // default no-op implementation
    fetchPivotPreviewValues: async () => [],
    initialData: {},
    setInitialData: () => { },
    resetForm: () => { },
    isEditMode: false,
    setIsEditMode: () => { },
    pivotDataEnabled: false,
    setPivotDataEnabled: () => { },
    availableColumns: [],
    setAvailableColumns: () => { },
});
export const DataTransferTemplateProvider = ({ children }) => {
    const translate = useLocalizeMessage();
    const toast = useToastNotification();
    const { entityId } = useContext(AuthContext);
    const webClient = useApiClient(ApiClientService.WEB);
    const queryClient = useQueryClient();
    const [templateCreationOption, setTemplateCreationOption] = useState('data_builder');
    const [prevTemplateCreationOption, setPrevTemplateCreationOption] = useState('data_builder');
    const [createTemplateFormData, setCreateTemplateFormData] = useState({});
    const [initialData, setInitialData] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isCreateFormValid, setIsCreateFormValid] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [pivotDataEnabled, setPivotDataEnabled] = useState(false);
    const [availableColumns, setAvailableColumns] = useState([]);
    // Remember the last study/environment used for executing SQL in sg-core
    const [lastQueryStudyId, setLastQueryStudyId] = useState('');
    const [lastQueryEnvironmentId, setLastQueryEnvironmentId] = useState('');
    const isCloneMode = templateCreationOption === 'clone';
    const getStatusBadge = (status) => {
        if (status === 'PUBLISHED') {
            return _jsx(Badge, { label: status, variant: 'success' });
        }
        if (status === 'DRAFT') {
            return _jsx(Badge, { label: status, variant: 'incomplete' });
        }
        return _jsx(Badge, { label: status, variant: 'common' });
    };
    const timezoneOptions = useMemo(() => [
        { label: translate('Patient local'), value: 'participant_time' },
        { label: translate('UTC'), value: 'UTC' },
    ], []);
    const packageTypeOptions = useMemo(() => [{ label: translate('One file with all tasks'), value: 'single' }], []);
    const fileExtensionOptions = useMemo(() => [
        { label: translate('CSV'), value: 'csv' },
        { label: translate('JSON'), value: 'json' },
        { label: translate('(TXT )(Semi-colon)'), value: 'csv-semi-colon' },
        { label: translate('(TXT )(Pipe)'), value: 'csv-pipe' },
        { label: translate('XML'), value: 'xml' },
        { label: translate('Excel'), value: 'excel' },
        { label: translate('HTML'), value: 'html' },
        { label: translate('LaTeX'), value: 'latex' },
        { label: translate('Markdown'), value: 'markdown' },
        { label: translate('XPT'), value: 'xpt' },
    ], []);
    const statusOptions = useMemo(() => [
        { label: translate('Published'), value: 'PUBLISHED' },
        { label: translate('Draft'), value: 'DRAFT' },
        { label: translate('Archived'), value: 'ARCHIVED' },
    ], []);
    const defaultFormValues = useMemo(() => ({
        name: '',
        description: '',
        packageType: packageTypeOptions[0].value,
        fileNamePattern: '{STUDY}-{YYYY}-{MM}-{DD}',
        archiveNamePattern: '{STUDY}-{YYYY}-{MM}-{DD}',
        fileExtension: fileExtensionOptions[0].value,
        stringQuotes: false,
        timezone: timezoneOptions[0].value,
        excludedTasks: [],
        excludedSteps: [],
        excludeInactiveTaskResponses: false,
        timeFormat: 'hh-mm-ss',
        dateFormat: 'YYYY-MM-DD',
        datetimeFormat: 'YYYY-MM-DD hh-mm-ss',
        customQuery: '',
        path: '',
        ownerId: entityId,
        isGlobal: false,
        jsonQuery: {},
        viewName: '',
        status: 'DRAFT',
        pivotDimensions: [],
        sortByDimensions: [],
        columns: [],
        numberFormat: 0,
        id: '',
        zipOutput: false,
        zipPassword: '',
        createdAt: Date.now(),
        updatedAt: Date.now(),
    }), [packageTypeOptions, fileExtensionOptions, timezoneOptions, entityId]);
    useEffect(() => {
        setCreateTemplateFormData(defaultFormValues);
        setInitialData(defaultFormValues);
    }, [defaultFormValues]);
    // Utility to get changed fields
    function getChangedFields(form, original) {
        const changed = {
            viewName: form.viewName,
        };
        if (templateCreationOption === 'data_builder') {
            changed.outputArrangement = form.outputArrangement;
            changed.jsonQuery = form.jsonQuery;
        }
        Object.keys(form).forEach((key) => {
            if (form[key] !== original[key]) {
                changed[key] = form[key];
            }
        });
        return changed;
    }
    const createDataTransferTemplate = async () => {
        try {
            if (!isCreateFormValid) {
                toast.addNotification({
                    type: 'error',
                    title: translate('Invalid form data'),
                });
                return;
            }
            setIsLoading(true);
            const requestMethod = isEditMode ? webClient.patch : webClient.post;
            const endpoint = isEditMode
                ? `/api/data_transfer/templates/${createTemplateFormData.id}`
                : '/api/data_transfer/templates';
            // Only send changed fields when editing
            const payload = isEditMode ? getChangedFields(createTemplateFormData, initialData) : createTemplateFormData;
            // BE generated fields
            if (isCloneMode) {
                delete payload.id;
                delete payload.path;
                delete payload.ownerId;
                delete payload.entityId;
                delete payload.createdAt;
                delete payload.updatedAt;
            }
            if (['sql_editor'].includes(templateCreationOption)) {
                payload.jsonQuery = undefined;
            }
            const { data } = await requestMethod(endpoint, payload);
            toast.addNotification({
                type: 'success',
                title: translate(`Transfer template ({name}) was {action} successfully.`, {
                    name: data.name,
                    action: isEditMode ? 'updated' : isCloneMode ? 'cloned' : 'created',
                }),
            });
            queryClient.invalidateQueries({
                queryKey: ['entities', entityId, 'transfer_templates'],
            });
            /*
              We need to invalidate this query to update the transfer template list in the data transfer page
              which has the key ['entities', entityId, 'studies', studyId, 'templates']
             */
            queryClient.invalidateQueries({
                predicate: (query) => {
                    const { queryKey } = query;
                    return queryKey[0] === 'entities' && queryKey[2] === 'studies' && queryKey[4] === 'templates';
                },
            });
            if (isEditMode) {
                queryClient.invalidateQueries({
                    queryKey: ['entities', entityId, 'transfer_templates', createTemplateFormData.id],
                });
            }
        }
        catch (error) {
            // The generic error toast is shown by our api client hook; log the server detail (e.g. the
            // response message) so create/update failures are diagnosable instead of silently swallowed.
            console.error('Failed to create/update transfer template:', error);
        }
        finally {
            setIsLoading(false);
        }
    };
    const checkIfTemplateNameIsValid = async (name) => {
        try {
            setIsLoading(true);
            // In edit mode, the initial name is valid, only if it changed we need to check again
            if (isEditMode && name === initialData.name) {
                return true;
            }
            const response = await webClient.post(`/api/data_transfer/templates/validations`, {
                isGlobal: false,
                name: name.trim(),
            });
            return response.status === 200;
        }
        catch {
            return false;
        }
        finally {
            setIsLoading(false);
        }
    };
    const validateSQLQuery = async ({ sql, explain, environmentId, studyId }) => {
        const trimmedSql = appendPreviewLimitIfMissing(sql);
        const response = await webClient.post(`/api/data_transfer/configs/query`, {
            sql: trimmedSql,
            explain,
            environmentId,
            studyId,
        });
        // Remember the last context used for queries so pivot preview can reuse it
        try {
            setLastQueryStudyId(studyId);
            setLastQueryEnvironmentId(environmentId);
        }
        catch { }
        return response;
    };
    const fetchPivotPreviewValues = async (pivotColumn) => {
        try {
            const baseSql = (createTemplateFormData?.customQuery || '').trim();
            if (!pivotColumn || !baseSql || !lastQueryEnvironmentId || !lastQueryStudyId) {
                return [];
            }
            // Ensure no trailing semicolon in the subquery
            const sanitizedBaseSql = baseSql.replace(/;\s*$/, '');
            // Quote the column to be safe
            const quotedCol = `"${pivotColumn.replace(/\"/g, '"')}"`;
            const wrappedSql = `SELECT ${quotedCol} AS pivot_column FROM (${sanitizedBaseSql}) t GROUP BY ${quotedCol} LIMIT 10`;
            const response = await webClient.post(`/api/data_transfer/configs/query`, {
                sql: wrappedSql,
                explain: false,
                environmentId: lastQueryEnvironmentId,
                studyId: lastQueryStudyId,
            });
            const rows = Array.isArray(response.data) ? response.data : [];
            const values = rows.map((r) => r.pivot_column).filter((v) => v !== undefined && v !== null);
            return values;
        }
        catch {
            return [];
        }
    };
    useEffect(() => {
        // Reset form data when template creation option actually changes (not just step navigation)
        if (!isEditMode && templateCreationOption !== prevTemplateCreationOption) {
            setCreateTemplateFormData(defaultFormValues);
            setPrevTemplateCreationOption(templateCreationOption);
        }
    }, [templateCreationOption, prevTemplateCreationOption, isEditMode, defaultFormValues]);
    useEffect(() => {
        // Only reset form data when entering edit mode and sync pivot enabled flag
        if (isEditMode) {
            setCreateTemplateFormData(initialData);
            const hasPivot = Boolean(initialData?.py?.operation);
            setPivotDataEnabled(hasPivot);
        }
    }, [isEditMode, initialData]);
    // Initialize form data with default values when not in edit mode and form is empty
    useEffect(() => {
        if (!isEditMode && (!createTemplateFormData || Object.keys(createTemplateFormData).length === 0)) {
            setCreateTemplateFormData(defaultFormValues);
        }
    }, [isEditMode, createTemplateFormData, defaultFormValues]);
    const resetForm = () => {
        setCreateTemplateFormData(defaultFormValues);
        setInitialData(defaultFormValues);
        setIsCreateFormValid(false);
        setIsLoading(false);
        setTemplateCreationOption('data_builder');
        setPrevTemplateCreationOption('data_builder');
        setIsEditMode(false);
        setPivotDataEnabled(false);
        setAvailableColumns([]);
        setLastQueryEnvironmentId('');
        setLastQueryStudyId('');
    };
    const value = useMemo(() => ({
        timezoneOptions,
        packageTypeOptions,
        fileExtensionOptions,
        templateCreationOption,
        setTemplateCreationOption,
        getStatusBadge,
        statusOptions,
        createTemplateFormData,
        setCreateTemplateFormData,
        isLoading,
        setIsLoading,
        createDataTransferTemplate,
        isCreateFormValid,
        setIsCreateFormValid,
        defaultFormValues,
        checkIfTemplateNameIsValid,
        validateSQLQuery,
        fetchPivotPreviewValues,
        initialData,
        setInitialData,
        resetForm,
        isEditMode,
        setIsEditMode,
        pivotDataEnabled,
        setPivotDataEnabled,
        availableColumns,
        setAvailableColumns,
    }), [
        timezoneOptions,
        packageTypeOptions,
        fileExtensionOptions,
        templateCreationOption,
        setTemplateCreationOption,
        getStatusBadge,
        statusOptions,
        createTemplateFormData,
        setCreateTemplateFormData,
        isLoading,
        setIsLoading,
        createDataTransferTemplate,
        isCreateFormValid,
        setIsCreateFormValid,
        defaultFormValues,
        checkIfTemplateNameIsValid,
        validateSQLQuery,
        initialData,
        setInitialData,
        resetForm,
        isEditMode,
        setIsEditMode,
        pivotDataEnabled,
        setPivotDataEnabled,
        availableColumns,
        setAvailableColumns,
    ]);
    return _jsx(DataTransferTemplateContext.Provider, { value: value, children: children });
};
